import { useState, useCallback, useRef } from "react";

export default function useFetchStages(api, endpoint, paramKey = "stage") {
    const [stages, setStages] = useState([]);
    const [activeStage, setActiveStage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // Guards against out-of-order responses: this is the shared stage-list
    // fetch behind all three tabs (Groups/Grids/Schedule). It's called on
    // mount, and again every time a settings save/reset forces a fresh
    // mount of the tab (Tournament.js remounts the active tab via
    // `key={refreshKey}`) - if an earlier call's response is slow enough to
    // land after a newer one, it silently overwrites the freshly-updated
    // stage list with the old one, making a just-created stage flash in
    // and then disappear.
    const reqRef = useRef(0);

    const fetchStages = useCallback(async () => {
        setIsLoading(true);
        reqRef.current += 1;
        const currentReq = reqRef.current;
        try {
            const res = await api.get(endpoint);
            if (reqRef.current !== currentReq) return;
            const data = res.data?.results || [];

            setStages(data);

            if (data.length) {
                const qIdx = parseInt(new URLSearchParams(window.location.search).get(paramKey)) - 1;
                const defaultStage = data[qIdx >= 0 && qIdx < data.length ? qIdx : 0].id;

                setActiveStage(prev => {
                    if (prev && data.some(s => s.id === prev)) return prev;
                    return defaultStage;
                });
                setIsLoading(false);
            } else {
                setActiveStage(null);
                setIsLoading(false);
            }
        } catch (e) {
            console.error(e);
            if (reqRef.current === currentReq) setIsLoading(false);
        }
    }, [api, endpoint, paramKey]);

    return {
        stages, setStages,
        activeStage, setActiveStage,
        isLoading, setIsLoading,
        fetchStages
    };
}
